import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { ArrowRight, Play } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const HeroSection = () => {
  const navigate = useNavigate();
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const heroHeight = window.innerHeight * 1.5;
      const progress = Math.min(window.scrollY / heroHeight, 1);
      setScrollProgress(progress);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const getPhase = () => {
    if (scrollProgress < 0.25) return 1;
    if (scrollProgress < 0.5) return 2;
    if (scrollProgress < 0.75) return 3;
    return 4;
  };

  const phase = getPhase();

  return (
    <section className="hero-section relative min-h-screen flex items-center justify-center overflow-hidden bg-black">
      {/* Animated Background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-transparent to-blue-600/5"></div>
        <div 
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: 'radial-gradient(circle at 50% 50%, rgba(59, 130, 246, 0.1) 0%, transparent 50%)',
            transform: `scale(${1 + scrollProgress * 0.3})`
          }}
        ></div>
      </div>

      {/* Content Container */}
      <div className="container mx-auto px-6 py-16 relative z-10">
        <div className="grid md:grid-cols-2 gap-8 items-center">
          {/* Left Side - Text Content */}
          <div 
            className="space-y-6"
            style={{
              opacity: 1 - scrollProgress * 0.5,
              transform: `translateY(${scrollProgress * 50}px)`
            }}
          >
            {/* Phase-based text transitions */}
            <div className="space-y-4">
              <h1 className="text-4xl md:text-5xl font-bold text-white leading-tight">
                {phase === 1 && (
                  <span className="animate-fade-in">
                    Tap. Connect. <br />
                    <span className="text-blue-500">Instantly.</span>
                  </span>
                )}
                {phase === 2 && (
                  <span className="animate-fade-in">
                    Share Your Contact <br />
                    <span className="text-blue-500">in Seconds</span>
                  </span>
                )}
                {phase === 3 && (
                  <span className="animate-fade-in">
                    No Apps. No QR Codes. <br />
                    <span className="text-blue-500">No Friction.</span>
                  </span>
                )}
                {phase === 4 && (
                  <span className="animate-fade-in">
                    The Future of <br />
                    <span className="text-blue-500">Networking</span>
                  </span>
                )}
              </h1>

              <p className="text-lg md:text-xl text-gray-400 max-w-xl">
                {phase === 1 && "Transform how you share your professional identity with smart NFC technology"}
                {phase === 2 && "One tap on your NFC keychain opens your full digital profile instantly"}
                {phase === 3 && "Works natively with all smartphones - zero downloads required"}
                {phase === 4 && "Join thousands of professionals who've upgraded their networking"}
              </p>
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Button 
                size="lg" 
                onClick={() => navigate('/products')}
                className="bg-blue-500 hover:bg-blue-600 text-white text-base px-6 py-5 group"
              >
                Shop NFC Solutions
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-2 border-zinc-700 text-white hover:bg-white/5 text-base px-6 py-5 group"
                onClick={() => {
                  document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                <Play className="mr-2 w-5 h-5 group-hover:scale-110 transition-transform" />
                See How It Works
              </Button>
            </div>

            {/* Trust Indicators */}
            <div className="flex items-center gap-6 pt-2">
              <div className="text-center">
                <div className="text-2xl font-bold text-white">10K+</div>
                <div className="text-xs text-gray-400">Happy Customers</div>
              </div>
              <div className="h-10 w-px bg-zinc-800"></div>
              <div className="text-center">
                <div className="text-2xl font-bold text-white">99%</div>
                <div className="text-xs text-gray-400">Satisfaction Rate</div>
              </div>
              <div className="h-10 w-px bg-zinc-800"></div>
              <div className="text-center">
                <div className="text-2xl font-bold text-white">24/7</div>
                <div className="text-xs text-gray-400">Support</div>
              </div>
            </div>
          </div>

          {/* Right Side - Product Visual */}
          <div 
            className="relative max-w-lg mx-auto"
            style={{
              transform: `translateY(${-scrollProgress * 100}px) rotateY(${scrollProgress * 45}deg) scale(${1 - scrollProgress * 0.2})`
            }}
          >
            {/* Main Product Image */}
            <div className="relative z-10">
              <div className="absolute -inset-6 bg-blue-500/20 blur-3xl rounded-full animate-pulse-slow"></div>
              <img 
                src="/images/realestate-hero.png"
                alt="Real Estate Professional with NFC Keychain"
                className="relative rounded-2xl shadow-2xl w-full h-auto max-h-96 object-cover transform hover:scale-105 transition-transform duration-500"
              />
            </div>

            {/* Floating Elements */}
            <div 
              className="absolute -top-8 -right-8 w-32 h-32 bg-blue-500/10 backdrop-blur-xl rounded-2xl border border-blue-500/20 flex items-center justify-center"
              style={{
                opacity: phase >= 2 ? 1 : 0,
                transform: `translateY(${phase >= 2 ? 0 : 20}px)`,
                transition: 'all 0.5s ease'
              }}
            >
              <div className="text-center">
                <div className="text-2xl font-bold text-white">📱</div>
                <div className="text-xs text-gray-400 mt-1">Tap Here</div>
              </div>
            </div>

            <div 
              className="absolute -bottom-8 -left-8 w-40 h-40 bg-zinc-900/80 backdrop-blur-xl rounded-2xl border border-zinc-700 p-4"
              style={{
                opacity: phase >= 3 ? 1 : 0,
                transform: `translateY(${phase >= 3 ? 0 : 20}px)`,
                transition: 'all 0.5s ease'
              }}
            >
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-blue-500"></div>
                  <div className="flex-1 h-3 bg-zinc-700 rounded"></div>
                </div>
                <div className="flex gap-2">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="w-8 h-8 rounded-lg bg-zinc-700"></div>
                  ))}
                </div>
                <div className="text-xs text-gray-400 text-center">Social Links</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 animate-bounce">
        <div className="w-6 h-10 border-2 border-zinc-700 rounded-full flex justify-center pt-2">
          <div className="w-1 h-3 bg-blue-500 rounded-full animate-scroll"></div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
